/**
 * Promise-based, one-shot helpers for imperative flows — `@captello/ulc-webview-sdk/promises`.
 *
 * Where {@link CaptelloWebview} is a long-lived client you subscribe to, these are
 * fire-once-and-await utilities that take an iframe directly: create a short-lived
 * client internally, wait for the relevant message, then tear it down. Ideal for
 * `await`-style code (e.g. "submit and get the body", "wait until the form loads").
 *
 * @example
 * import { submitForm, waitForFormLoad } from "@captello/ulc-webview-sdk/promises";
 *
 * await waitForFormLoad(iframe, { targetOrigin });
 * const body = await submitForm(iframe, { targetOrigin });
 */

import { CaptelloWebview } from "./client";
import type { CaptelloWebviewOptions, FrameLike } from "./client";
import { OutboundMessageType } from "./messages";
import type { OutboundMessageMap, SubmissionBody } from "./messages";

export { SubmissionError, SubmissionTimeoutError } from "./client";

/** Frame accepted by the helpers — an `<iframe>` or anything exposing `contentWindow`. */
type ElementOrFrame = FrameLike;

/** Options shared by every promise helper. */
export interface WaitOptions extends Pick<CaptelloWebviewOptions, "targetOrigin" | "hostWindow" | "matchSource"> {
    /**
     * How long to wait before rejecting. Defaults to 60_000ms. Pass `0` or `Infinity`
     * to wait indefinitely (the caller is then responsible for not leaking the wait).
     */
    timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 60_000;

/** Rejection reason from {@link waitForMessage} / {@link waitForFormLoad} on timeout. */
export class MessageTimeoutError extends Error {
    constructor(
        public readonly messageType: string,
        public readonly timeoutMs: number,
    ) {
        super(`Timed out after ${timeoutMs}ms waiting for "${messageType}" from the Captello webview.`);
        this.name = "MessageTimeoutError";
    }
}

function clientOptions(options: WaitOptions): CaptelloWebviewOptions {
    return {
        targetOrigin: options.targetOrigin,
        hostWindow: options.hostWindow,
        matchSource: options.matchSource,
        // One-shot helpers act on a form assumed already loaded; never buffer their
        // sends waiting for a form_load_complete that may have already fired.
        queueUntilReady: false,
    };
}

/**
 * Resolves with the next outbound message of `type` from the webview, or rejects with
 * a {@link MessageTimeoutError} if none arrives within the timeout. The internal
 * listener is always removed before settling.
 *
 * @example const msg = await waitForMessage(iframe, OutboundMessageType.SubmissionBody, { targetOrigin });
 */
export function waitForMessage<T extends OutboundMessageType>(
    frame: ElementOrFrame,
    type: T,
    options: WaitOptions = {},
): Promise<OutboundMessageMap[T]> {
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    return new Promise<OutboundMessageMap[T]>((resolve, reject) => {
        const client = new CaptelloWebview(frame, clientOptions(options));
        let timer: ReturnType<typeof setTimeout> | undefined;

        const settle = (fn: () => void) => {
            if (timer !== undefined) clearTimeout(timer);
            client.destroy();
            fn();
        };

        client.once(type, (message) => settle(() => resolve(message)));

        if (timeoutMs > 0 && timeoutMs !== Infinity) {
            timer = setTimeout(() => settle(() => reject(new MessageTimeoutError(type, timeoutMs))), timeoutMs);
        }
    });
}

/**
 * Resolves once the webview reports `form_load_complete`, or rejects with a
 * {@link MessageTimeoutError} on timeout.
 *
 * Note: this only catches a *future* load event. If the form may have already loaded
 * before you call this (e.g. you attach late), prefer subscribing with a long-lived
 * {@link CaptelloWebview} created before the iframe navigates.
 */
export function waitForFormLoad(frame: ElementOrFrame, options: WaitOptions = {}): Promise<void> {
    return waitForMessage(frame, OutboundMessageType.FormLoadComplete, options).then(() => undefined);
}

/**
 * Submits the form and awaits the outcome: resolves with the {@link SubmissionBody} on
 * `submission_body`, rejects with a `SubmissionError` (translated message) on
 * `form_error_message`, or a `SubmissionTimeoutError` if neither arrives in time.
 *
 * Standalone equivalent of {@link CaptelloWebview.submitAndWait} for code that doesn't
 * hold a long-lived client — it creates one, submits, and tears it down.
 *
 * @example
 * try {
 *   const body = await submitForm(iframeRef.current!, { targetOrigin });
 *   await persist(body);
 * } catch (err) {
 *   if (err instanceof SubmissionError) showToast(err.message);
 * }
 */
export function submitForm(frame: ElementOrFrame, options: WaitOptions = {}): Promise<SubmissionBody> {
    const client = new CaptelloWebview(frame, clientOptions(options));
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    return client.submitAndWait(timeoutMs).finally(() => client.destroy());
}
